/**
 * 분류 유형 관리 — **증상 · 원인 · 처방** 유형을 늘리고 다듬는다.
 *
 * 축의 뜻(사용자 정의):
 *   증상  시간 지연을 **직접적으로 초래하는 현상**
 *   원인  그 증상의 원인
 *   처방  원인을 직접적 혹은 간접적으로 해소하는 방안
 *
 * 이 정의를 화면이 들고 있어야 한다 — 경계가 사람마다 다르면 같은 사례가 제각각 분류되고,
 * 그러면 분류를 붙이는 값어치가 사라진다. 그래서 축 이름 옆에 뜻을 그대로 적는다.
 *
 * ## 지우는 버튼이 없다
 *
 * 이미 붙은 사례의 분류가 사라지면 그 사례는 어떤 검색으로도 안 걸리는데, 그 사실은 아무에게도
 * 안 보인다. 그래서 **내리기(비활성)** 만 있다 — 새로 고를 수 없을 뿐, 붙어 있던 것은 그대로
 * 남고 그대로 검색된다. 몇 건에 붙어 있는지를 함께 보여 줘서 내리기 전에 알 수 있게 한다.
 *
 * ## 내장 유형은 축을 못 바꾼다
 *
 * 코드가 실행계획 증상과 이어져 있어(서버 `FROM_PLAN_SYMPTOM`), 축이 바뀌면 화면이
 * "이 실행계획의 증상" 을 보여 주고도 그 사례를 못 찾게 된다. 이름·설명·키워드는 얼마든지 고친다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiPlus, FiSave, FiEyeOff, FiEye, FiLoader } from 'react-icons/fi';
import { getCaseTopics, saveCaseTopic } from '../../services/assistantClient';

const EMPTY = { code: '', axis: 'symptom', label: '', desc: '', keywords: '', support: false };
/** 유형 코드 형식 — 백엔드 tuningCase/taxonomy.js 의 CODE_RE 와 같아야 한다 */
const CODE_RE = /^[A-Za-z][A-Za-z0-9_]{1,39}$/;

function TopicManager() {
    const { t } = useTranslation();
    const [axes, setAxes] = useState([]);
    const [topics, setTopics] = useState([]);
    const [usage, setUsage] = useState({});
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // 고치는 중인 유형 (code 로 찾는다)
    const [draft, setDraft] = useState(EMPTY);      // 새로 만드는 유형
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await getCaseTopics(true);     // 내려 둔 것까지 — 여기서만 보인다
            setAxes(r.axes || []);
            setTopics(r.topics || []);
            setUsage(r.usage || {});
        } catch (err) {
            toast.error(err.message);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const byAxis = useMemo(() => {
        const m = {};
        for (const tp of topics) (m[tp.axis] = m[tp.axis] || []).push(tp);
        return m;
    }, [topics]);

    const save = useCallback(async (code, body) => {
        setBusy(true);
        try {
            await saveCaseTopic(code, body);
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    }, [load]);

    const addNew = useCallback(async () => {
        if (!draft.code.trim() || !draft.label.trim()) {
            toast.warning(t('assistant:topicManager.needCodeLabel'));
            return;
        }
        await save(draft.code.trim(), {
            axis: draft.axis, label: draft.label.trim(), desc: draft.desc.trim(), keywords: draft.keywords,
            // 보조 처방 — 처방 축에서만 뜻이 있다(힌트처럼 다른 처방을 돕는 구현 수단)
            support: draft.axis === 'fix' ? draft.support : false,
        });
        setDraft(EMPTY);
    }, [draft, save, t]);

    // 추가 양식의 확인 — 코드 형식은 서버 taxonomy.CODE_RE 와 같다(다르면 서버가 거절할 것을 화면이 통과시킨다)
    const draftAxis = axes.find(a => a.id === draft.axis);
    const codeTrim = draft.code.trim();
    const codeTaken = !!codeTrim && topics.some(tp => tp.code === codeTrim);
    const codeBad = !!codeTrim && (!CODE_RE.test(codeTrim) || codeTaken);
    const canAdd = !!draft.label.trim() && !!codeTrim && !codeBad;

    if (loading) return <div className="tcase-empty">{t('common:state.loading')}</div>;

    return (
        <div className="tcf tm">
            {axes.map(axis => (
                <section className="tcf-step" key={axis.id}>
                    <div className="tcf-head">
                        {t(`assistant:topicAxis.${axis.id}`)}
                        {axis.optional ? <span className="tcf-axisOpt">{t('assistant:caseMeta.axisOptional')}</span> : null}
                        {/* 축의 뜻을 그대로 적는다 — 경계가 흔들리면 분류가 쓸모없어진다 */}
                        <span className="tm-axisDesc">{axis.desc}</span>
                    </div>
                    <table className="tm-table">
                        <tbody>
                            {(byAxis[axis.id] || []).map(tp => (
                                <tr key={tp.code} className={tp.active ? '' : 'tm-off'}>
                                    {editing === tp.code ? (
                                        <TopicRowEdit
                                            topic={tp} busy={busy} t={t}
                                            onCancel={() => setEditing(null)}
                                            onSave={(body) => save(tp.code, body)}
                                        />
                                    ) : (
                                        <>
                                            <td className="tm-label">
                                                {tp.label}
                                                {tp.support ? <span className="tcf-axisOpt" title={t('assistant:topicManager.supportHint')}>{t('assistant:topicManager.support')}</span> : null}
                                            </td>
                                            <td className="tm-code"><code>{tp.code}</code></td>
                                            <td className="tm-desc">{tp.desc}</td>
                                            <td className="tm-use">
                                                {usage[tp.code] ? t('assistant:topicManager.used', { count: usage[tp.code] }) : ''}
                                            </td>
                                            <td className="tm-act">
                                                <button type="button" className="tcf-btn" onClick={() => setEditing(tp.code)}>
                                                    {t('common:ui.edit')}
                                                </button>
                                                {/* 지우지 않고 내린다 — 붙어 있던 분류는 그대로 남는다 */}
                                                <button
                                                    type="button" className="tcf-btn" disabled={busy}
                                                    title={t(tp.active ? 'assistant:topicManager.hideHint' : 'assistant:topicManager.showHint')}
                                                    onClick={() => save(tp.code, { active: !tp.active })}
                                                >
                                                    {tp.active ? <FiEyeOff size={11} /> : <FiEye size={11} />}
                                                    {t(tp.active ? 'assistant:topicManager.hide' : 'assistant:topicManager.show')}
                                                </button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            ))}

            {/* 유형 추가 — 칸마다 이름표를 둔 양식(2026-10-24 사용자 요청: 이름표 없이 입력란만 늘어서 무엇을 적는지 알기 어려웠다).
                축을 고르면 그 축의 뜻이 바로 옆에 보이고, 코드는 형식이 맞아야 「추가」 가 눌린다(서버 taxonomy.CODE_RE 와 같은 규칙) */}
            <section className="tcf-step tm-add">
                <div className="tcf-head">{t('assistant:topicManager.add')}</div>
                <div className="tm-form">
                    <label className="tm-flabel" htmlFor="tm-axis">{t('assistant:topicManager.fAxis')}</label>
                    <div className="tm-fval">
                        <select id="tm-axis" className="tcf-select" value={draft.axis} onChange={(e) => setDraft({ ...draft, axis: e.target.value })}>
                            {axes.map(a => <option key={a.id} value={a.id}>{t(`assistant:topicAxis.${a.id}`)}</option>)}
                        </select>
                        {draftAxis && draftAxis.desc ? <span className="tm-fnote">{draftAxis.desc}</span> : null}
                        {draft.axis === 'fix' ? (
                            <label className="tm-check" title={t('assistant:topicManager.supportHint')}>
                                <input type="checkbox" checked={draft.support} onChange={(e) => setDraft({ ...draft, support: e.target.checked })} />
                                {t('assistant:topicManager.supportLong')}
                            </label>
                        ) : null}
                    </div>

                    <label className="tm-flabel" htmlFor="tm-label">{t('assistant:topicManager.fLabel')}<em>*</em></label>
                    <div className="tm-fval">
                        <input id="tm-label" className="tcf-input" value={draft.label}
                            placeholder={t('assistant:topicManager.labelPlaceholder')}
                            onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
                    </div>

                    <label className="tm-flabel" htmlFor="tm-code">{t('assistant:topicManager.fCode')}<em>*</em></label>
                    <div className="tm-fval">
                        <input id="tm-code" className={`tcf-input tcf-mono tm-code-in${codeBad ? ' tm-bad' : ''}`} value={draft.code}
                            placeholder={t('assistant:topicManager.codePlaceholder')}
                            onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
                        <span className={`tm-fnote${codeBad ? ' tm-badText' : ''}`}>
                            {codeTaken ? t('assistant:topicManager.codeTaken') : t('assistant:topicManager.codeRule')}
                        </span>
                    </div>

                    <label className="tm-flabel" htmlFor="tm-desc">{t('assistant:topicManager.fDesc')}</label>
                    <div className="tm-fval">
                        <input id="tm-desc" className="tcf-input" value={draft.desc}
                            placeholder={t('assistant:topicManager.descPlaceholder')}
                            onChange={(e) => setDraft({ ...draft, desc: e.target.value })} />
                    </div>

                    <label className="tm-flabel" htmlFor="tm-kw">{t('assistant:topicManager.fKeywords')}</label>
                    <div className="tm-fval tm-fcol">
                        <input id="tm-kw" className="tcf-input" value={draft.keywords}
                            placeholder={t('assistant:topicManager.keywordsPlaceholder')}
                            onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} />
                        <span className="tm-fnote">{t('assistant:topicManager.keywordsHint')}</span>
                    </div>
                </div>
                <div className="tm-formFoot">
                    {(draft.label || draft.code || draft.desc || draft.keywords) ? (
                        <button type="button" className="tcf-btn" disabled={busy} onClick={() => setDraft({ ...EMPTY, axis: draft.axis })}>
                            {t('assistant:topicManager.clearForm')}
                        </button>
                    ) : null}
                    <button type="button" className="tcf-btn tcf-primary" disabled={busy || !canAdd} onClick={addNew}>
                        {busy ? <FiLoader size={11} className="tcf-spin" /> : <FiPlus size={11} />}
                        {t('assistant:topicManager.add')}
                    </button>
                </div>
            </section>
        </div>
    );
}

/** 한 줄 편집. 코드와 축은 못 바꾼다 — 코드는 사례가 참조하고, 축은 실행계획과 이어져 있다. */
function TopicRowEdit({ topic, busy, onSave, onCancel, t }) {
    const [label, setLabel] = useState(topic.label);
    const [desc, setDesc] = useState(topic.desc);
    const [keywords, setKeywords] = useState((topic.keywords || []).join(', '));
    return (
        <td colSpan={5}>
            <div className="tcf-row">
                <input className="tcf-input tm-w1" value={label} onChange={(e) => setLabel(e.target.value)} />
                <code className="tm-code">{topic.code}</code>
                <input className="tcf-input" value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            <div className="tcf-row">
                <input className="tcf-input" value={keywords} onChange={(e) => setKeywords(e.target.value)} />
                <button type="button" className="tcf-btn tcf-primary" disabled={busy} onClick={() => onSave({ label, desc, keywords })}>
                    <FiSave size={11} /> {t('assistant:caseForm.save')}
                </button>
                <button type="button" className="tcf-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
            </div>
        </td>
    );
}

export default TopicManager;
